import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  signal,
  input,
  output,
  viewChild,
} from '@angular/core';

import { AngularSvgIconModule } from 'angular-svg-icon';
import { AppButton, ButtonType } from '../../button/button.component';

interface DropdownOption {
  label: string;
  value: string;
  group?: string;
}

type DropdownValue = string | string[] | null;

@Component({
  selector: 'app-mobile-searchable-picker',
  standalone: true,
  imports: [AngularSvgIconModule, AppButton],
  templateUrl: './mobile-searchable-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        animation: picker-fade-in 150ms ease;
      }
      @keyframes picker-fade-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      /* -webkit-overflow-scrolling is a no-op since iOS 13; pan-y keeps horizontal swipes from navigating back. */
      .picker-options-list {
        touch-action: pan-y;
        -webkit-overflow-scrolling: touch;
      }
    `,
  ],
})
export class MobileSearchablePickerComponent implements OnInit, AfterViewInit {
  readonly title = input.required<string>();
  readonly options = input.required<DropdownOption[]>();
  readonly currentValue = input<DropdownValue>(null);
  readonly multiple = input(false);

  readonly commit = output<DropdownValue>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly cancel = output<void>();

  readonly searchInput =
    viewChild.required<ElementRef<HTMLInputElement>>('searchInput');

  protected readonly ButtonType = ButtonType;
  private readonly destroyRef = inject(DestroyRef);

  readonly searchTerm = signal('');
  readonly workingSelection = signal<DropdownValue>(null);
  // Mirror `visualViewport.offsetTop` / `.height` (iOS PWA keyboard shifts the visible area), and 0 without `visualViewport`.
  readonly viewportTop = signal<number>(0);
  readonly viewportHeight = signal<number>(0);

  readonly filteredOptions = computed(() => {
    const term = this.searchTerm().toLowerCase();
    if (!term) return this.options();
    return this.options().filter(o => o.label.toLowerCase().includes(term));
  });

  private touchStartY = 0;
  private touchStartTime = 0;

  ngOnInit(): void {
    // Recreated per open cycle by the parent `@if`, so `workingSelection` is seeded once.
    this.workingSelection.set(this.currentValue());

    if (typeof window === 'undefined') return;

    // iOS WebKit scrolls the document when the keyboard rises, displacing elements with `position` set to fixed, and pinning body prevents it.
    const scrollY = window.scrollY;
    const body = document.body;
    const previousBodyStyle = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    this.destroyRef.onDestroy(() => {
      body.style.position = previousBodyStyle.position;
      body.style.top = previousBodyStyle.top;
      body.style.left = previousBodyStyle.left;
      body.style.right = previousBodyStyle.right;
      body.style.width = previousBodyStyle.width;
      window.scrollTo(0, scrollY);
    });

    if (window.visualViewport) {
      const vv = window.visualViewport;
      const update = () => {
        this.viewportTop.set(vv.offsetTop);
        this.viewportHeight.set(vv.height);
      };
      update();
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
      this.destroyRef.onDestroy(() => {
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
      });
    }
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.searchInput().nativeElement.focus());
  }

  onSearchInput(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  onOptionTap(option: DropdownOption): void {
    if (!this.multiple()) {
      this.commit.emit(option.value);
      return;
    }

    const current = this.workingSelection();
    const currentArr = Array.isArray(current) ? current : [];
    const idx = currentArr.indexOf(option.value);
    if (idx === -1) {
      this.workingSelection.set([...currentArr, option.value]);
    } else {
      this.workingSelection.set(currentArr.filter(v => v !== option.value));
    }
  }

  onDone(): void {
    this.commit.emit(this.workingSelection());
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartY = event.touches[0].clientY;
    this.touchStartTime = Date.now();
  }

  onTouchEnd(event: TouchEvent): void {
    const dy = event.changedTouches[0].clientY - this.touchStartY;
    const elapsed = Date.now() - this.touchStartTime;
    if (dy >= 80 && elapsed < 400) {
      this.cancel.emit();
    }
  }

  isSelected(option: DropdownOption): boolean {
    const selection = this.workingSelection();
    if (Array.isArray(selection)) {
      return selection.includes(option.value);
    }
    return selection === option.value;
  }
}
